/* Hosts the authenticated mobile/tablet trading shell after the browser login flow has completed. */
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {QepQueryClientProvider} from '@qep/shared/src';
import {ClipboardList, Ticket} from 'lucide-react';
import './MobileApp.scss';
import MobileTradePage from './pages/Trade/mobile-trade-page.js';
import MobileOrdersPage from './pages/Orders/mobile-orders-page.js';
import {type MobileOrderSubmission} from './mobile-order-types.js';
import {sortMobileOrdersNewestFirst, upsertMobileSessionOrder} from './mobile-order-state.js';
import {useMobileOrderSummary} from './mobile-order-summary.js';
import {shouldUseTabletSplitLayout} from './device.js';

export type Page = 'Trade' | 'Orders';

/** Tracks whether the already-selected mobile shell has enough width for the tablet split layout. */
function useTabletSplitLayout(): boolean {
  const [isTabletSplit, setIsTabletSplit] = useState(() => shouldUseTabletSplitLayout());

  useEffect(() => {
    /** Recomputes tablet split state when the mobile shell viewport changes. */
    function handleResize() {
      setIsTabletSplit(shouldUseTabletSplitLayout());
    }

    globalThis.addEventListener('resize', handleResize);
    return () => {
      globalThis.removeEventListener('resize', handleResize);
    };
  }, []);

  return isTabletSplit;
}

/** Renders the mobile-only tab button with stable labels and icon affordances. */
function MobileTabButton({
  page,
  activePage,
  onSelect,
  children,
}: {
  page: Page;
  activePage: Page;
  onSelect: (page: Page) => void;
  children: React.ReactNode;
}) {
  const isActive = activePage === page;

  return (
    <button
      type='button'
      className={`mobile-tab-button ${isActive ? 'active' : ''}`}
      aria-selected={isActive}
      onClick={() => {
        onSelect(page);
      }}
    >
      {children}
      <span>{page}</span>
    </button>
  );
}

/** Provides the compact mobile order shell, using tabs on phones and split panes on tablets. */
const MobileApp: React.FC = () => {
  const [activePage, setActivePage] = useState<Page>('Trade');
  const [orders, setOrders] = useState<MobileOrderSubmission[]>([]);
  const isTabletSplit = useTabletSplitLayout();
  const orderSummary = useMobileOrderSummary();

  const newestOrders = useMemo(
    () => sortMobileOrdersNewestFirst(orders),
    [orders],
  );

  const handleOrderSubmitted = useCallback((order: MobileOrderSubmission) => {
    setOrders(previous => upsertMobileSessionOrder(previous, order));
  }, []);

  const tradePage = (
    <MobileTradePage
      orders={orders}
      onOrderSubmitted={handleOrderSubmitted}
    />
  );
  const ordersPage = (
    <MobileOrdersPage
      orders={newestOrders}
      summaryErrorMessage={orderSummary.errorMessage}
      summaryOrders={orderSummary.orders}
    />
  );

  return (
    <QepQueryClientProvider>
      <div className={`mobile-app-container ${isTabletSplit ? 'tablet-split' : 'phone-tabs'}`} data-testid='mobile-app'>
        {isTabletSplit
          ? (
            <main className='mobile-tablet-grid'>
              <section className='mobile-pane mobile-pane-trade'>{tradePage}</section>
              <section className='mobile-pane mobile-pane-orders'>{ordersPage}</section>
            </main>
          )
          : (
            <>
              <main className='mobile-phone-main mobile-phone-scroll'>
                {activePage === 'Trade' ? tradePage : ordersPage}
              </main>
              <nav className='mobile-bottom-nav' aria-label='Mobile order navigation'>
                <MobileTabButton page='Trade' activePage={activePage} onSelect={setActivePage}>
                  <Ticket aria-hidden='true' size={22} />
                </MobileTabButton>
                <MobileTabButton page='Orders' activePage={activePage} onSelect={setActivePage}>
                  <ClipboardList aria-hidden='true' size={22} />
                </MobileTabButton>
              </nav>
            </>
          )}
      </div>
    </QepQueryClientProvider>
  );
};

export default MobileApp;
